// src/services/firebase.js
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore, collection, doc, getDoc, setDoc, updateDoc, deleteDoc,
  onSnapshot, query, where, orderBy, limit, getDocs, addDoc, serverTimestamp
} from 'firebase/firestore';
import { getDatabase, ref, onValue } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

const app = initializeApp(firebaseConfig);

// App Check — opt-in only (REACT_APP_ENABLE_APP_CHECK=true)
const appCheckEnabled = process.env.REACT_APP_ENABLE_APP_CHECK === 'true';
const recaptchaKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_KEY;
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_APPCHECK_DEBUG_TOKEN) {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
}
if (appCheckEnabled && recaptchaKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (appCheckEnabled && !recaptchaKey) {
  console.warn('REACT_APP_ENABLE_APP_CHECK is true but REACT_APP_RECAPTCHA_ENTERPRISE_KEY is missing.');
}

export const getAdminAuthErrorMessage = (error) => {
  const code = error?.code || '';
  const msg = error?.message || '';
  if (code.includes('requests-from-referer') || msg.includes('referer')) {
    return 'This domain is blocked by your Firebase API key. In Google Cloud Console → Credentials, add https://admin.mytownly.in/* to HTTP referrers.';
  }
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'Invalid email or password.';
  }
  if (code === 'auth/too-many-requests') return 'Too many attempts. Wait a few minutes.';
  if (code === 'auth/network-request-failed') return 'Network error. Check your connection.';
  return msg || 'Sign-in failed. Try again.';
};

export const auth = getAuth(app);
export const db = getFirestore(app);
export const rtdb = getDatabase(app);
const functions = getFunctions(app, 'asia-south1');

const DEFAULT_PLATFORM = {
  maxOrderTotal: 1000,
  serviceRadiusKm: 3,
  storeLat: 13.6561836,
  storeLng: 78.9397511,
  deliveryFeeTiers: [
    { maxMeters: 500, fee: 20 },
    { maxMeters: 1000, fee: 30 },
    { maxMeters: 2000, fee: 40 },
    { maxMeters: 3000, fee: 50 },
  ],
};

// ─── ADMIN LOGGING ────────────────────────────
export const logAdminAction = async (action, target, details = {}) => {
  const user = auth.currentUser;
  if (!user) return;
  await addDoc(collection(db, 'adminLogs'), {
    action,
    target,
    details,
    adminId: user.uid,
    adminEmail: user.email || '',
    createdAt: serverTimestamp(),
  });
};

export const listenToAdminLogs = (callback) => {
  const q = query(collection(db, 'adminLogs'), orderBy('createdAt', 'desc'), limit(200));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

// ─── PLATFORM CONFIG ──────────────────────────
export const listenToPlatformSettings = (callback) => {
  return onSnapshot(doc(db, 'config', 'platform'), (snap) => {
    const data = snap.exists() ? snap.data() : {};
    callback({
      maxOrderTotal: data.maxOrderTotal ?? DEFAULT_PLATFORM.maxOrderTotal,
      serviceRadiusKm: data.serviceRadiusKm ?? DEFAULT_PLATFORM.serviceRadiusKm,
      storeLat: data.storeLat ?? data.storeLocation?.lat ?? DEFAULT_PLATFORM.storeLat,
      storeLng: data.storeLng ?? data.storeLocation?.lng ?? DEFAULT_PLATFORM.storeLng,
      deliveryFeeTiers: data.deliveryFeeTiers?.length ? data.deliveryFeeTiers : DEFAULT_PLATFORM.deliveryFeeTiers,
    });
  });
};

export const savePlatformSettings = async (form) => {
  await setDoc(doc(db, 'config', 'platform'), {
    maxOrderTotal: Number(form.maxOrderTotal),
    serviceRadiusKm: Number(form.serviceRadiusKm),
    storeLat: Number(form.storeLat),
    storeLng: Number(form.storeLng),
    storeLocation: { lat: Number(form.storeLat), lng: Number(form.storeLng) },
    deliveryFeeTiers: form.deliveryFeeTiers,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  await logAdminAction('save_platform_settings', 'config/platform', form);
};

// ─── PROMOS ───────────────────────────────────
export const listenToPromos = (callback) => {
  const q = query(collection(db, 'promos'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const addPromo = async (data) => {
  const ref = await addDoc(collection(db, 'promos'), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  await logAdminAction('create_promo', `promos/${ref.id}`, data);
  return ref.id;
};

export const updatePromo = async (id, data) => {
  await updateDoc(doc(db, 'promos', id), { ...data, updatedAt: serverTimestamp() });
  await logAdminAction('update_promo', `promos/${id}`, data);
};

export const deletePromo = async (id) => {
  await deleteDoc(doc(db, 'promos', id));
  await logAdminAction('delete_promo', `promos/${id}`, {});
};

// ─── PAYOUTS ──────────────────────────────────
export const listenToPayouts = (callback) => {
  const q = query(collection(db, 'payouts'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const addPayout = async (data) => {
  const ref = await addDoc(collection(db, 'payouts'), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  await logAdminAction('create_payout', `payouts/${ref.id}`, data);
  return ref.id;
};

/** Idempotent daily merchant payout — doc id m_{shopId}_{period} */
export const addMerchantDailyPayout = async (data) => {
  const docId = `m_${data.merchantId}_${data.period}`;
  const ref = doc(db, 'payouts', docId);
  const existing = await getDoc(ref);
  if (existing.exists()) return { id: docId, skipped: true };
  await setDoc(ref, { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  await logAdminAction('create_payout', `payouts/${docId}`, data);
  return { id: docId, skipped: false };
};

export const updatePayout = async (id, data) => {
  await updateDoc(doc(db, 'payouts', id), { ...data, updatedAt: serverTimestamp() });
  await logAdminAction('update_payout', `payouts/${id}`, data);
};

export const deletePayout = async (id) => {
  await deleteDoc(doc(db, 'payouts', id));
  await logAdminAction('delete_payout', `payouts/${id}`, {});
};

// ─── CALLABLES ────────────────────────────────
export const adminNotifyRiders = async (payload = {}) => {
  const fn = httpsCallable(functions, 'adminNotifyRiders');
  const result = await fn(payload);
  await logAdminAction('notify_riders', 'riders', payload);
  return result.data;
};

// ─── AUTH ─────────────────────────────────────
export const adminLogin = (email, password) => signInWithEmailAndPassword(auth, email, password);
export const adminLogout = () => signOut(auth);
export const onAuthChange = (cb) => onAuthStateChanged(auth, cb);

/** True if this UID is in Firestore `admin` or `admins` (matches firestore.rules). */
export const checkIsAdmin = async (uid) => {
  if (!uid) return false;
  const [legacy, modern] = await Promise.all([
    getDoc(doc(db, 'admin', uid)),
    getDoc(doc(db, 'admins', uid)),
  ]);
  return legacy.exists() || modern.exists();
};

export const listenToGroceryStats = (callback) => {
  const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(500));
  return onSnapshot(q, (snap) => {
    const orders = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      .filter((o) => o.status !== 'payment_pending');
    const activeOrders = orders.filter((o) => !['delivered', 'cancelled'].includes(o.status));
    const deliveredOrders = orders.filter((o) => o.status === 'delivered');
    const groceryRevenue = deliveredOrders.reduce((s, o) => s + (o.total || 0), 0);
    callback({ orders, activeOrders, deliveredOrders, groceryRevenue });
  });
};

// ─── DASHBOARD STATS (rides) ──────────────────
export const listenToStats = (callback) => {
  const unsubRides = onSnapshot(collection(db, 'rides'), (snap) => {
    const rides = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const activeRides = rides.filter(r => ['searching', 'driver_assigned', 'arrived', 'started'].includes(r.status));
    const completedRides = rides.filter(r => r.status === 'completed');
    const totalRevenue = completedRides.reduce((s, r) => s + (r.fare || 0), 0);
    callback({ rides, activeRides, completedRides, totalRevenue });
  });
  return unsubRides;
};

// ─── RIDES ────────────────────────────────────
export const listenToRides = (statusFilter, callback) => {
  let q;
  if (statusFilter && statusFilter !== 'all') {
    q = query(collection(db, 'rides'), where('status', '==', statusFilter), orderBy('createdAt', 'desc'));
  } else {
    q = query(collection(db, 'rides'), orderBy('createdAt', 'desc'), limit(50));
  }
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

export const updateRide = (rideId, data) => updateDoc(doc(db, 'rides', rideId), { ...data, updatedAt: serverTimestamp() });

// ─── RIDERS ───────────────────────────────────
export const listenToRiders = (callback, onError) => {
  return onSnapshot(collection(db, 'riders'), (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, onError);
};

export const updateRider = async (riderId, data) => {
  await updateDoc(doc(db, 'riders', riderId), { ...data, updatedAt: serverTimestamp() });
  await logAdminAction('update_rider', `riders/${riderId}`, data);
};
export const deleteRider = async (riderId) => {
  await deleteDoc(doc(db, 'riders', riderId));
  await logAdminAction('delete_rider', `riders/${riderId}`, {});
};

// ─── USERS ────────────────────────────────────
export const listenToUsers = (callback) => {
  return onSnapshot(query(collection(db, 'users'), orderBy('updatedAt', 'desc')), (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

export const updateUser = async (userId, data) => {
  await updateDoc(doc(db, 'users', userId), { ...data, updatedAt: serverTimestamp() });
  const action = data.isBlocked === true ? 'block_user' : data.isBlocked === false ? 'unblock_user' : 'update_user';
  await logAdminAction(action, `users/${userId}`, data);
};
export const deleteUser = async (userId) => {
  await deleteDoc(doc(db, 'users', userId));
  await logAdminAction('delete_user', `users/${userId}`, {});
};

// ─── ORDERS (grocery) ─────────────────────────
export const listenToOrders = (callback) => {
  const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(500));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

export const updateOrder = async (orderId, data) => {
  await updateDoc(doc(db, 'orders', orderId), { ...data, updatedAt: serverTimestamp() });
  await logAdminAction('update_order', `orders/${orderId}`, data);
};

// ─── SHOPS ────────────────────────────────────
export const listenToShops = (callback) => {
  return onSnapshot(collection(db, 'shops'), (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

export const addShop = async (data) => {
  const ref = await addDoc(collection(db, 'shops'), { ...data, createdAt: serverTimestamp() });
  await logAdminAction('create_shop', `shops/${ref.id}`, data);
  return ref.id;
};
export const updateShop = async (shopId, data) => {
  await updateDoc(doc(db, 'shops', shopId), data);
  await logAdminAction('update_shop', `shops/${shopId}`, data);
};
export const deleteShop = async (shopId) => {
  await deleteDoc(doc(db, 'shops', shopId));
  await logAdminAction('delete_shop', `shops/${shopId}`, {});
};

// ─── PRODUCTS ─────────────────────────────────
export const listenToProducts = (callback) => {
  return onSnapshot(collection(db, 'products'), (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

export const addProduct = (data) => addDoc(collection(db, 'products'), { ...data, createdAt: serverTimestamp() });
export const updateProduct = (id, data) => updateDoc(doc(db, 'products', id), data);
export const deleteProduct = (id) => deleteDoc(doc(db, 'products', id));

// ─── LIVE DRIVERS (Realtime DB) ───────────────
export const listenToLiveDrivers = (callback) => {
  const driversRef = ref(rtdb, 'driverLocations');
  return onValue(driversRef, (snap) => {
    const data = snap.val();
    callback(data ? Object.entries(data).map(([id, v]) => ({ id, ...v })) : []);
  });
};

/** Grocery delivery — riders set isOnline on Firestore (admin cannot read all RTDB locations). */
export const listenToOnlineRiders = (callback, onError) => {
  return onSnapshot(collection(db, 'riders'), (snap) => {
    const riders = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((r) => r.isOnline === true && r.isApproved === true && r.isBlocked !== true);
    callback(riders);
  }, onError);
};
